import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Outlet, useLocation } from "react-router-dom";

import { useSelectedFacility } from "@/app/FacilityContext";

/**
 * Facility-selection gate (F2 Layer-3). Sits INSIDE the dashboard shell wrapping
 * the routed <Outlet/>, AFTER F1's RequireApprovedOrg (Layer 2). It never owns
 * the org lock and never issues a redirect — it only swaps the Outlet for an
 * inline NoFacilitySelected prompt on operational pages with no facility chosen.
 *
 *   showContent = facilityId || isOrgLevelRoute(pathname)
 *
 * Org-level routes are reachable WITHOUT a facility selection; operational pages
 * (e.g. /dashboard, /courts) require one.
 */

/** Routes reachable WITHOUT a selected facility (CURRENT app routes). */
export const ORG_LEVEL_PREFIXES = [
  "/org",
  "/getting-started",
  "/pending",
  "/settings/facility",
  "/settings/org",
  // Platform contract review — an org ↔ platform fact, never facility-scoped.
  "/settings/contract",
  "/facilities/new",
] as const;

export function isOrgLevelRoute(pathname: string): boolean {
  return ORG_LEVEL_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

function NoFacilitySelected() {
  return (
    <Box
      component="section"
      aria-labelledby="no-facility-title"
      sx={{
        display: "grid",
        minHeight: "50vh",
        placeItems: "center",
        px: 2,
        py: 6,
        textAlign: "center",
      }}
    >
      <Stack spacing={1.5} sx={{ maxWidth: 440, alignItems: "center" }}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "action.hover",
            color: "text.secondary",
          }}
        >
          <BusinessOutlinedIcon aria-hidden="true" />
        </Avatar>
        <Typography id="no-facility-title" component="h1" variant="h5">
          No facility selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose a facility from the switcher in the sidebar to manage its
          courts, bookings, and staff.
        </Typography>
      </Stack>
    </Box>
  );
}

export function FacilityGate() {
  const { facilityId } = useSelectedFacility();
  const { pathname } = useLocation();

  const showContent = Boolean(facilityId) || isOrgLevelRoute(pathname);

  return showContent ? <Outlet /> : <NoFacilitySelected />;
}
